import { ReactNode } from 'react'
import { Button } from '@/components/ui/button'
import { ArrowLeft } from 'lucide-react'
import { Search } from "@/components/ui/search";

/**
 * StepHeader - Standardized header component for install flow steps
 *
 * Enforces consistent structure:
 * [Back Button] [Title] [Search Bar?] [Actions?]
 *
 * All install flow steps should use this component in their header
 * to ensure visual and behavioral consistency.
 */

export interface StepHeaderProps {
  /**
   * Title text or custom title component
   * Will be displayed prominently and takes flex-1 space
   */
  title: string | ReactNode

  description?: string

  /**
   * Handler for back button click
   * Back button is always present in the same position
   */
  onBack?: () => void

  /**
   * Optional search bar configuration
   * When provided, renders a fixed-width search input
   */
  searchBar?: {
    placeholder?: string
    value: string
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  }

  /**
   * Optional custom actions (buttons, etc.) to display on the right
   * Will be rendered after title and search bar
   */
  actions?: ReactNode
}

export function StepHeader({ title, description, onBack, searchBar, actions }: StepHeaderProps) {
  return (
    <div className="flex flex-col items-start gap-4 px-2">
      <div className="flex items-baseline gap-4">
        {/* Back button */}
        {
          !!onBack && (
            <Button variant="ghost" size="sm" onClick={onBack} aria-label="Go back">
              <ArrowLeft className="h-4 w-4"/>
            </Button>
          )
        }

        {/* Title - takes available space */}
        <div className="flex flex-col items-start gap-2">
          <h2 className="flex-1">{title}</h2>
          {
            description && (
              <div className="text-muted-foreground text-sm">{description}</div>
            )
          }
        </div>
      </div>

      {/* Optional search bar */}
      {searchBar && (
        <Search
          placeholder={searchBar.placeholder || 'Search...'}
          value={searchBar.value}
          onChange={searchBar.onChange}
          className="w-full"
        />
      )}

      {/* Optional custom actions */}
      {actions}
    </div>
  )
}
